/**
 * AR Playbook Authentication Helpers
 *
 * Centralized helpers for token management and role extraction
 * for business user authentication.
 */

import { BUSINESS_USER_TOKEN } from 'apis/arPlaybookBaseQuery'

import { TBusinessUserRole } from '../types/businessUserRole'

interface JWTPayload {
    data?: {
        id?: string
        username?: string
        role?: TBusinessUserRole
    }
    exp?: number
}

/**
 * Get business user token from storage
 * Checks sessionStorage first (ghost login), then localStorage (normal login)
 */
export const getBusinessUserToken = (tokenKey = BUSINESS_USER_TOKEN): string | null => {
    return sessionStorage.getItem(tokenKey) || localStorage.getItem(tokenKey)
}

export const removeBusinessUserToken = (tokenKey = BUSINESS_USER_TOKEN): void => {
    if (sessionStorage.getItem(tokenKey)) {
        sessionStorage.removeItem(tokenKey)
        return
    }
    localStorage.removeItem(tokenKey)
}

/**
 * Parse JWT payload from token
 */
export const parseBusinessUserToken = (token: string): JWTPayload | null => {
    try {
        return JSON.parse(atob(token.split('.')[1])) as JWTPayload
    } catch {
        return null
    }
}

/**
 * Get user role from stored token
 * Returns null if no token or role not found
 */
export const getBusinessUserRole = (): TBusinessUserRole | null => {
    const token = getBusinessUserToken()
    if (!token) {
        return null
    }

    const payload = parseBusinessUserToken(token)
    const userData = payload?.data
    if (typeof userData === 'object' && userData !== null && userData.role) {
        return userData.role
    }
    return null
}

/**
 * Check if business user is currently logged in
 */
export const isBusinessUserLoggedIn = (): boolean => {
    const token = getBusinessUserToken()
    if (!token) {
        return false
    }

    // Optionally check token expiration
    const payload = parseBusinessUserToken(token)
    if (payload?.exp) {
        const currentTime = Math.floor(Date.now() / 1000)
        return payload.exp > currentTime
    }

    return true
}

/**
 * Get user data from stored token
 */
export const getBusinessUserData = (): JWTPayload['data'] | null => {
    const token = getBusinessUserToken()
    if (!token) {
        return null
    }

    const payload = parseBusinessUserToken(token)
    return payload?.data ?? null
}
